import { useCallback, useEffect } from 'react';
import { type EventSourceMessage } from '@microsoft/fetch-event-source';
import { useMercureSubscription } from './useMercureSubscription';
import { MERCURE_TOPICS } from '@/constants/mercureTopics';
import { subscribeToMockSocketEvents } from '@/services/mercure/mockSocketEvents';
import { isCallMockAllowed } from '@/utils/isCallMockAllowed';
import { parseSocketEvent } from '@/utils/parseSocketEvent';
import { type ICallEvent } from '@/types/ICallEvent';
import { type ICallHistoryEvent } from '@/types/ICallHistoryEvent';
import { type ISocketOrderEvent } from '@/types/ISocketOrderEvent';

interface IUseMercureEventsParams {
  onOrderEvent: (event: ISocketOrderEvent) => void;
  onCallEvent?: (event: ICallEvent) => void;
  onCallHistoryEvent?: (event: ICallHistoryEvent) => void;
  enabled: boolean;
}

/**
 * Доменный хук единой подписки на Mercure: одно SSE-соединение на все топики
 * приложения. Поверх базового `useMercureSubscription` разбирает сырое
 * сообщение через `parseSocketEvent` и разводит его по обработчикам согласно
 * `kind`. Бизнес-логику не содержит — остаётся «тупым».
 *
 * Обработчики звонков опциональны: топики уже в подписке, а потребители
 * подключаются по мере готовности UI.
 *
 * @param params.onOrderEvent - обработчик события заказа
 * @param params.onCallEvent - обработчик живого события звонка
 * @param params.onCallHistoryEvent - обработчик исторического события звонка
 * @param params.enabled - запускать подписку только когда `true`
 */
export const useMercureEvents = ({
  onOrderEvent,
  onCallEvent,
  onCallHistoryEvent,
  enabled
}: IUseMercureEventsParams): void => {
  const handleRawEvent = useCallback(
    (raw: string) => {
      const socketEvent = parseSocketEvent(raw);

      if (socketEvent === null) {
        return;
      }

      switch (socketEvent.kind) {
        case 'order':
          onOrderEvent(socketEvent.event);
          break;
        case 'call':
          onCallEvent?.(socketEvent.event);
          break;
        case 'callHistory':
          onCallHistoryEvent?.(socketEvent.event);
          break;
      }
    },
    [onOrderEvent, onCallEvent, onCallHistoryEvent]
  );

  const handleMessage = useCallback(
    (message: EventSourceMessage) => {
      handleRawEvent(message.data);
    },
    [handleRawEvent]
  );

  useMercureSubscription({
    topics: MERCURE_TOPICS,
    onMessage: handleMessage,
    enabled
  });

  // Тестовый канал: кадр от кнопки-эмулятора проходит тот же разбор, что и
  // настоящий. При выключенном флаге подписки нет вовсе.
  useEffect(() => {
    if (!isCallMockAllowed()) {
      return;
    }

    return subscribeToMockSocketEvents(handleRawEvent);
  }, [handleRawEvent]);
};
